import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { Formik, FormikHelpers } from "formik";
import React, { useRef, useState } from "react";
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";

import AuthApi from "../../api/AuthApi";
import { appConstants } from "../../config/app-constants";
import { EToastvariant, IResetPwdAPI, IResetPwdForm } from "../../config/interfaces";
import { useAppDispatch } from "../../redux/Store";
import { showToastMsg } from "../../redux/slice/ToastMsgSlice";
import { StorageService, UtilService } from "../../services";
import FASvgIcon from "../FASvgIcon";
import { AlertModal } from "../modals";
import PasswordHintOverlay from "../overlay/PasswordHintOverlay";
import { ButtonSpinner } from "../placeholders";

const initialValues = {
  password: "",
  confirmPassword: ""
};

const validationSchema = Yup.object({
  password: Yup.string()
    .trim()
    .matches(appConstants.PASSWORD_REGEX, "Invalid Password")
    .required("Password is required"),
  confirmPassword: Yup.string()
    .trim()
    .oneOf([Yup.ref("password")], "Passwords must match")
    .required("Confirm Password is required")
});

const ResetPasswordForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const pwdOverlayTargetRef = useRef<HTMLInputElement | null>(null);

  const [showHintOverlay, setShowHintOverlay] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState<boolean>(false);
  const [showAlertModal, setShowAlertModal] = useState<boolean>(false);

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const toggleShowConfirmPwd = () => {
    setShowConfirmPwd(!showConfirmPwd);
  };

  const handleResetPwdFormSubmit = async (
    values: IResetPwdForm,
    { setSubmitting }: FormikHelpers<IResetPwdForm>
  ) => {
    try {
      setSubmitting(true);

      const verifyData = await StorageService.getPostVerifyData();
      if (!verifyData) {
        return;
      }

      const _verifyData = JSON.parse(verifyData);
      const _values: IResetPwdAPI = {
        newPassword: values.password,
        postVerificationToken: _verifyData.postVerificationToken
      };
      const authApi = AuthApi.getInstance();
      await authApi.resetPasswordByToken(_values);

      await new Promise((r) => setTimeout(r, 500));
      setShowAlertModal(() => true);
    } catch (error) {
      dispatch(
        showToastMsg({
          isVisible: true,
          title: "Error",
          message: UtilService.getErrorMessage(error),
          variant: EToastvariant.Danger
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Container>
        <div className="auth-form-wrap">
          <Row className="align-items-center justify-content-md-center">
            <Col>
              <Card bg="white" className="auth-card">
                <Card.Body>
                  <h1 className="fw-bold text-center mb-1">Reset Password</h1>
                  <p className="text-center text-greyDark">
                    Lost your Password? Please enter new password
                  </p>
                  <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleResetPwdFormSubmit}
                  >
                    {({
                      handleChange,
                      handleBlur,
                      handleSubmit,
                      values,
                      errors,
                      touched,
                      isSubmitting
                    }) => (
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleSubmit();
                        }}
                      >
                        <Form.Group className="form-group" controlId="password">
                          <Form.Label className="required-field">Enter New Password</Form.Label>
                          <div className="field-has-icon" ref={pwdOverlayTargetRef}>
                            <Form.Control
                              type={showPassword ? "text" : "password"}
                              name="password"
                              isInvalid={!!errors.password && !!touched.password}
                              value={values.password}
                              placeholder="***********"
                              onChange={handleChange}
                              onFocus={() => {
                                setShowHintOverlay(true);
                              }}
                              onBlur={(e) => {
                                handleBlur(e);
                                setShowHintOverlay(false);
                              }}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.password}
                            </Form.Control.Feedback>
                            <Button
                              variant="btn"
                              className="field-icon"
                              onClick={toggleShowPassword}
                            >
                              <FASvgIcon icon={showPassword ? faEye : faEyeSlash} />
                            </Button>
                          </div>
                          <PasswordHintOverlay
                            show={showHintOverlay}
                            targetRef={pwdOverlayTargetRef}
                          />
                        </Form.Group>

                        <Form.Group className="form-group" controlId="confirmPassword">
                          <Form.Label className="required-field">Confirm New Password</Form.Label>
                          <div className="field-has-icon">
                            <Form.Control
                              type={showConfirmPwd ? "text" : "password"}
                              name="confirmPassword"
                              onBlur={handleBlur}
                              isInvalid={!!errors.confirmPassword && !!touched.confirmPassword}
                              value={values.confirmPassword}
                              placeholder="***********"
                              onChange={handleChange}
                              autoComplete="confirmPassword"
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.confirmPassword}
                            </Form.Control.Feedback>
                            <Button
                              variant="btn"
                              className="field-icon"
                              onClick={toggleShowConfirmPwd}
                            >
                              <FASvgIcon icon={showConfirmPwd ? faEye : faEyeSlash} />
                            </Button>
                          </div>
                        </Form.Group>

                        <div className="btn-wrap">
                          <Button
                            variant="primary"
                            type="submit"
                            size="lg"
                            className="w-100 shadow"
                            disabled={isSubmitting}
                          >
                            {!isSubmitting ? "Reset Password" : <ButtonSpinner />}
                          </Button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </Card.Body>
              </Card>

              <div className="auth-footer-1 justify-content-end">
                <Form.Text className="m-0 text-greyDark">Back to</Form.Text>
                <Button variant="outline-greyDark" size="sm" onClick={() => navigate("/login")}>
                  Sign In
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      </Container>

      {showAlertModal && (
        <AlertModal
          showModal={showAlertModal}
          iconColorClass="text-success"
          title="Password reset successful"
          message={<p>Awesome, you have successfully updated your password.</p>}
          onYesBtnClick={() => navigate("/login", { replace: true })}
          onYesBtnText="Back to Sign In"
        />
      )}
    </>
  );
};

export default ResetPasswordForm;
